import React from 'react';

/**
 * The two monsters on the kiosk's "How are you eating today?" screen.
 *
 * Same family as the ad-slot monster on the attract screen (AdSlotMonster):
 * the orange blob, the maroon eyes, the two antennae — so a guest who watched
 * one stand on the poster slide recognises the pair that now ask the
 * question. Each one is dressed for its answer rather than labelled with an
 * icon: the dine-in one is the waiter, in a chef's toque and a bow tie,
 * holding a covered plate up for the table; the take-away one holds a paper
 * bag out in both hands, offering it to whoever is standing at the panel.
 *
 * Drawn, not downloaded: one SVG each, scaling to a 43-inch panel without
 * going soft, coloured from the kiosk's own tokens. Everything that moves is
 * CSS (kiosk.css, `.k-mon-*`), so it all stops under prefers-reduced-motion.
 * Both are decorative — the button they sit in carries the words — so they
 * are hidden from assistive tech.
 */

/* The parts the two share: eyes that blink and look, cheeks, and the ground
   shadow. Kept as render helpers rather than components so neither monster
   remounts when the parent re-renders (see the CustomerMenu lesson). */
const eyes = () => (
  <g className="k-mon-blink">
    <ellipse className="k-mon-eye" cx="128" cy="150" rx="29" ry="32" />
    <ellipse className="k-mon-eye" cx="196" cy="150" rx="29" ry="32" />
    <g className="k-mon-pupils">
      <circle className="k-mon-pupil" cx="128" cy="152" r="13" />
      <circle className="k-mon-pupil" cx="196" cy="152" r="13" />
      <circle className="k-mon-glint" cx="133" cy="146" r="4.5" />
      <circle className="k-mon-glint" cx="201" cy="146" r="4.5" />
    </g>
  </g>
);

const cheeks = () => (
  <>
    <circle className="k-mon-cheek" cx="98" cy="188" r="12" />
    <circle className="k-mon-cheek" cx="226" cy="188" r="12" />
  </>
);

/** The chef-waiter: toque, bow tie, a covered plate held high, a napkin over
    the other arm. */
export function DineInMonster() {
  return (
    <svg className="k-mon-art" viewBox="0 0 320 320" aria-hidden="true" focusable="false">
      <ellipse className="k-mon-shadow" cx="160" cy="292" rx="80" ry="12" />

      <g className="k-mon-body">
        {/* The toque, drawn before the body so its band sits on the head.
            Three puffs on top, a band below — it wobbles a beat behind the
            bob, which is what makes it look like cloth rather than paint. */}
        <g className="k-mon-hat">
          <path className="k-mon-toque"
            d="M104 92 C96 60 118 42 140 52 C150 28 184 28 194 52 C216 42 238 62 226 92 Z" />
          <rect className="k-mon-toque-band" x="104" y="86" width="122" height="22" rx="8" />
        </g>

        {/* The body: a soft blob, wider at the foot. */}
        <path className="k-mon-skin"
          d="M160 86 C222 86 252 128 252 180 C252 236 216 266 160 266
             C104 266 68 236 68 180 C68 128 98 86 160 86 Z" />

        {/* Left arm (the viewer's left): a napkin folded over the forearm —
            the waiter's badge of office. It sways with the bob. */}
        <g className="k-mon-arm-napkin">
          <path className="k-mon-limb" d="M74 190 C50 198 40 220 44 240" />
          <path className="k-mon-napkin" d="M46 214 L78 206 L82 246 L50 254 Z" />
        </g>

        {/* Right arm, raised, carrying the plate and its cloche. The whole
            tray lifts and settles on its own clock, slightly out of step with
            the body, so the dome is being carried rather than glued on. */}
        <g className="k-mon-arm-tray">
          <path className="k-mon-limb" d="M246 184 C270 170 284 148 286 120" />
          <g className="k-mon-tray">
            <ellipse className="k-mon-plate" cx="286" cy="112" rx="46" ry="11" />
            <path className="k-mon-cloche" d="M246 110 C246 78 266 62 286 62 C306 62 326 78 326 110 Z" />
            <circle className="k-mon-cloche-knob" cx="286" cy="58" r="6" />
            <path className="k-mon-cloche-shine" d="M258 98 C260 84 270 74 280 70" />
            {/* Steam leaking out from under the dome — something hot is on it. */}
            <path className="k-mon-steam k-mon-steam-a" d="M268 56 C262 46 274 40 268 30" />
            <path className="k-mon-steam k-mon-steam-b" d="M286 50 C280 40 292 34 286 24" />
            <path className="k-mon-steam k-mon-steam-c" d="M304 56 C298 46 310 40 304 30" />
          </g>
        </g>

        {eyes()}
        {cheeks()}
        {/* A grin, open — a closed curve reads as a smirk at this size. */}
        <path className="k-mon-mouth" d="M134 194 C148 214 176 214 190 194 Z" />

        {/* The bow tie, under the chin: two wings and a knot. */}
        <g className="k-mon-bowtie">
          <path d="M160 236 L132 224 L132 250 Z" />
          <path d="M160 236 L188 224 L188 250 Z" />
          <circle cx="160" cy="237" r="7" />
        </g>

        <path className="k-mon-limb" d="M126 266 L126 282" />
        <path className="k-mon-limb" d="M194 266 L194 282" />
      </g>
    </svg>
  );
}

/** The one at the counter: a cap, and a paper bag held out in both hands
    towards whoever is standing there. */
export function TakeAwayMonster() {
  return (
    <svg className="k-mon-art" viewBox="0 0 320 320" aria-hidden="true" focusable="false">
      <ellipse className="k-mon-shadow" cx="160" cy="292" rx="80" ry="12" />

      <g className="k-mon-body">
        {/* Antennae, with a bulb each — they lag behind the bob. */}
        <g className="k-mon-antenna">
          <path className="k-mon-limb" d="M126 96 C118 66 104 56 96 48" />
          <circle className="k-mon-bulb" cx="94" cy="44" r="11" />
        </g>
        <g className="k-mon-antenna k-mon-antenna-b">
          <path className="k-mon-limb" d="M194 96 C202 66 216 56 224 48" />
          <circle className="k-mon-bulb" cx="226" cy="44" r="11" />
        </g>

        <path className="k-mon-skin"
          d="M160 86 C222 86 252 128 252 180 C252 236 216 266 160 266
             C104 266 68 236 68 180 C68 128 98 86 160 86 Z" />

        {/* A little cap with a visor, worn straight — counter staff, not chef. */}
        <g className="k-mon-cap">
          <path className="k-mon-cap-crown" d="M112 100 C112 72 136 60 160 60 C184 60 208 72 208 100 Z" />
          <path className="k-mon-cap-visor" d="M100 100 L220 100 C224 110 212 116 196 114 L124 114 C108 116 96 110 100 100 Z" />
        </g>

        {eyes()}
        {cheeks()}
        <path className="k-mon-mouth" d="M134 194 C148 214 176 214 190 194 Z" />

        {/* Feet first, so the bag held in front of them covers them. */}
        <path className="k-mon-limb" d="M126 266 L126 282" />
        <path className="k-mon-limb" d="M194 266 L194 282" />

        {/* Both arms swing out from the sides and round to the bag, which is
            held low in front — below the face, so the grin stays on show —
            and pushed out towards the viewer on a loop: it drops a little and
            grows a little, the way a thing does when it is held out to you.
            The hands are drawn over the bag so it is gripped, not stuck on.
            The sparkles say "fresh" without a word. */}
        <g className="k-mon-offer">
          <path className="k-mon-limb" d="M72 206 C36 222 40 268 104 270" />
          <path className="k-mon-limb" d="M248 206 C284 222 280 268 216 270" />
          <g className="k-mon-bag">
            <path className="k-mon-bag-handle" d="M136 238 C136 218 184 218 184 238" />
            <path className="k-mon-bag-body" d="M110 240 L210 240 L204 302 L116 302 Z" />
            <path className="k-mon-bag-fold" d="M110 240 L210 240 L206 256 L114 256 Z" />
            {/* A tiny heart on the bag — somebody packed it with care. */}
            <path className="k-mon-bag-mark"
              d="M160 292 C150 284 142 276 148 268 C153 262 160 268 160 272 C160 268 167 262 172 268 C178 276 170 284 160 292 Z" />
          </g>
          <circle className="k-mon-hand" cx="106" cy="270" r="14" />
          <circle className="k-mon-hand" cx="214" cy="270" r="14" />
          <g className="k-mon-sparkles">
            <path className="k-mon-sparkle k-mon-sparkle-a" d="M52 150 L56 162 L68 166 L56 170 L52 182 L48 170 L36 166 L48 162 Z" />
            <path className="k-mon-sparkle k-mon-sparkle-b" d="M272 140 L275 148 L283 151 L275 154 L272 162 L269 154 L261 151 L269 148 Z" />
            <path className="k-mon-sparkle k-mon-sparkle-c" d="M292 232 L295 240 L303 243 L295 246 L292 254 L289 246 L281 243 L289 240 Z" />
          </g>
        </g>
      </g>
    </svg>
  );
}
